import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Globe,
  Target,
  User,
  Heart,
  Rocket,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';
import { services } from '../data/mockData';
import { Link } from 'react-router-dom';

const Services = () => {
  const iconMap = {
    Globe,
    Target,
    User,
    Heart,
    Rocket
  };

  return (
    <div className="min-h-screen bg-slate-950 pt-24 pb-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-amber-500/10 text-amber-400 border-amber-500/20">
            Our Services
          </Badge>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Comprehensive Web Solutions
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            From concept to launch, we provide end-to-end web design and development services
            tailored to your business goals.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {services.map((service) => {
            const IconComponent = iconMap[service.icon] || Globe;

            return (
              <Card
                key={service.id}
                className="bg-slate-800/50 border-slate-700 hover:border-amber-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-amber-500/10"
              >
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="w-14 h-14 bg-gradient-to-br from-amber-400/20 to-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-7 h-7 text-amber-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-white mb-2">
                        {service.title}
                      </h3>
                      <p className="text-amber-400 font-semibold">{service.price}</p>
                    </div>
                  </div>

                  <p className="text-gray-300 mb-6">{service.description}</p>

                  <div className="mb-6">
                    <h4 className="text-white font-semibold mb-3">Key Features:</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {service.features.map((feature, index) => (
                        <div key={index} className="flex items-start space-x-2">
                          <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-400 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-slate-900/50 rounded-lg p-4 mb-6">
                    <div className="text-sm text-gray-400 mb-2">
                      <span className="font-semibold text-white">Benefits: </span>
                      {service.benefits}
                    </div>
                    <div className="text-sm text-gray-400">
                      <span className="font-semibold text-white">Ideal for: </span>
                      {service.idealFor}
                    </div>
                  </div>

                  <Link to="/contact">
                    <Button className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold">
                      Get This Service
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Additional Services Section */}
        <div className="bg-slate-900/50 rounded-2xl p-8 md:p-12 border border-slate-800">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white mb-4">
              Additional Services & Support
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Beyond core web development, we offer comprehensive support services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-slate-800/30 rounded-lg p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-2">
                Domain & Hosting Setup
              </h3>
              <p className="text-gray-400 text-sm">
                Complete assistance with domain registration and hosting configuration
              </p>
            </div>

            <div className="bg-slate-800/30 rounded-lg p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-2">
                Website Maintenance
              </h3>
              <p className="text-gray-400 text-sm">
                Ongoing support, updates, and security monitoring for your website
              </p>
            </div>

            <div className="bg-slate-800/30 rounded-lg p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-2">
                Content Migration
              </h3>
              <p className="text-gray-400 text-sm">
                Seamless transfer of content from your old website to the new one
              </p>
            </div>

            <div className="bg-slate-800/30 rounded-lg p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-2">
                Analytics Setup
              </h3>
              <p className="text-gray-400 text-sm">
                Google Analytics and tracking configuration for data-driven insights
              </p>
            </div>

            <div className="bg-slate-800/30 rounded-lg p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-2">
                Training & Documentation
              </h3>
              <p className="text-gray-400 text-sm">
                Comprehensive training to manage and update your website independently
              </p>
            </div>

            <div className="bg-slate-800/30 rounded-lg p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-2">
                Rush Delivery
              </h3>
              <p className="text-gray-400 text-sm">
                Need it faster? We offer expedited delivery for urgent projects
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <h2 className="text-3xl font-bold text-white mb-4">
            Not Sure Which Service You Need?
          </h2>
          <p className="text-gray-400 mb-8">
            Book a free consultation and we'll help you choose the perfect solution
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold px-8 shadow-lg shadow-amber-500/20"
            >
              Book Free Consultation
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Services;
